/**
 * Application Constants
 * Centralized configuration and constants
 */

// Normalize API base URL: must be absolute (include scheme) or browser treats it as relative path
const rawBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'
const normalizedBaseUrl =
  rawBaseUrl.startsWith('http://') || rawBaseUrl.startsWith('https://')
    ? rawBaseUrl
    : `https://${rawBaseUrl.replace(/^\/+/, '')}`

// API Configuration
export const API_CONFIG = {
  BASE_URL: normalizedBaseUrl,
  ENDPOINTS: {
    UPLOAD: '/api/upload',
    HEALTH: '/api/health',
    TEST_EMAIL: '/api/test-email',
  },
  TIMEOUT: 30000, // 30 seconds
}

// Application Routes
export const ROUTES = {
  HOME: '/',
  PRICING: '/pricing',
  UPLOAD: '/upload',
  DOWNLOAD: '/download',
  ADMIN_LOGIN: '/admin/login',
  ADMIN_DASHBOARD: '/admin',
  ADMIN_SUBMISSIONS: '/admin/submissions',
  ADMIN_ORDERS: '/admin/orders',
}

// Match Types
export const MATCH_TYPES = {
  TURF: 'Turf',
  T20: 'T20',
  ODI: 'ODI',
  TEST: 'Test',
  GULLY: 'Gully Cricket',
  OTHER: 'Other',
}

// Focus Types
export const FOCUS_TYPES = {
  BATTING: 'Batting',
  BOWLING: 'Bowling',
  ALL_ROUNDER: 'All-rounder',
  FIELDING: 'Fielding',
  TEAM: 'Team Performance',
}

// Pricing Plans (Per Hour Rates)
export const PRICING_PLANS = [
  {
    id: 'reel-only',
    name: 'Reel Only',
    pricePerHour: 999, // Base price per hour
    description: 'Perfect for Instagram',
    features: [
      '4-5 Instagram Reel (vertical)',
      'Best moments compilation',
      'Music & effects',
      '48-72 hour delivery',
    ],
    popular: false,
  },
  {
    id: 'full-highlight',
    name: 'Full Highlight',
    pricePerHour: 1999, // Base price per hour
    description: 'Complete package',
    features: [
      'Instagram Reel (vertical)',
      'YouTube Short (vertical)',
      'YouTube Highlight Video (cinematic)',
      'Player-focused edits available',
      '48-72 hour delivery',
    ],
    popular: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    pricePerHour: 2999, // Base price per hour
    description: 'Everything + more',
    features: [
      'Everything in Full Highlight',
      'Multiple player-focused edits',
      'Custom music selection',
      'Priority editing',
      '24-48 hour delivery',
      'Unlimited revisions',
    ],
    popular: false,
  },
]

// Helper function to calculate total price
export const calculatePrice = (planId, hours) => {
  const plan = PRICING_PLANS.find(p => p.id === planId)
  if (!plan) return 0
  return plan.pricePerHour * hours
}

// Helper function to format price
export const formatPrice = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

// How It Works Steps
export const HOW_IT_WORKS_STEPS = [
  {
    number: '01',
    title: 'Paste Drive Link',
    description: 'Upload your full match recording via Google Drive. Just paste the link and we\'ll handle the rest.',
    icon: '📎',
  },
  {
    number: '02',
    title: 'We Edit Like Pros',
    description: 'AI + human editors pick the best moments. Cinematic cuts, crowd sounds, slow-mo, and epic music.',
    icon: '🎬',
  },
  {
    number: '03',
    title: 'Get Social-Ready Videos',
    description: 'Receive Reels, Shorts, and YouTube highlights. Ready to post and go viral.',
    icon: '🚀',
  },
]

// Why Choose Us Reasons
export const WHY_REASONS = [
  {
    icon: '🏆',
    title: 'Feel like an IPL highlight',
    description: 'Professional-grade editing that makes you look like a star',
  },
  {
    icon: '✨',
    title: 'Your name. Your moment.',
    description: 'Personalized edits that celebrate your best plays',
  },
  {
    icon: '📱',
    title: 'Perfect for Instagram, YouTube & WhatsApp',
    description: 'Optimized formats for every platform you use',
  },
  {
    icon: '🎯',
    title: 'Edited by people who understand cricket',
    description: 'We know what moments matter. We know cricket.',
  },
]

// App Metadata
export const APP_CONFIG = {
  NAME: 'Reel Cricket',
  TAGLINE: 'You play the match. We create the magic.',
  DESCRIPTION: 'Turn your full match recordings into thrilling social-media-ready highlight videos',
  CONTACT_EMAIL: import.meta.env.VITE_CONTACT_EMAIL || 'contact@reelcricket.com',
}

// Animation Durations (in seconds)
export const ANIMATION_DURATION = {
  FAST: 0.3,
  NORMAL: 0.6,
  SLOW: 1.0,
}

// Form Validation
export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  GOOGLE_DRIVE_REGEX: /^https:\/\/(drive\.google\.com|docs\.google\.com)/,
  MIN_NAME_LENGTH: 2,
  MAX_NAME_LENGTH: 100,
}

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error: Could not connect to server. Please make sure the backend is running.',
  GENERIC_ERROR: 'Something went wrong. Please try again.',
  REQUIRED_FIELD: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_DRIVE_LINK: 'Please enter a valid Google Drive link',
  SUBMISSION_FAILED: 'Failed to submit your match. Please try again.',
}

// Success Messages
export const SUCCESS_MESSAGES = {
  SUBMISSION_SUCCESS: 'Your match has been submitted successfully!',
  EMAIL_SENT: 'Confirmation email sent successfully',
}
